/* me:tale — landing page B (shock&feed) — GSAP + ScrollTrigger + Lenis */
(() => {
  'use strict';

  const root = document.documentElement;
  const loader = document.querySelector('.loader');

  // Jeśli CDN z GSAP się nie załadował — pokaż stronę bez animacji
  if (!window.gsap || !window.ScrollTrigger) {
    loader && loader.remove();
    root.classList.remove('is-loading');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(pointer: fine)').matches;
  const isMobile = () => matchMedia('(max-width: 900px)').matches;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const clamp01 = v => Math.max(0, Math.min(1, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeInOut = t => (t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
  const easeOut = t => 1 - Math.pow(1 - t, 3);

  /* ---------------- smooth scroll ---------------- */
  let lenis = null;
  if (!reduce && window.Lenis) {
    lenis = new Lenis({ lerp: 0.085, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    lenis.stop();
  }
  $$('a[href^="#"]').forEach(a => a.addEventListener('click', e => {
    const id = a.getAttribute('href');
    if (id === '#') { e.preventDefault(); return; }
    const target = id === '#top' ? 0 : $(id);
    if (target === null) return;
    e.preventDefault();
    if (lenis) lenis.scrollTo(target, { duration: 1.8 });
    else if (target === 0) scrollTo({ top: 0, behavior: 'smooth' });
    else target.scrollIntoView({ behavior: 'smooth' });
  }));

  /* ---------------- text scramble ---------------- */
  const GLYPHS = 'ABCDEFGHIJKLMNOPRSTUWYZĄĘŁŚŻ0123456789:/—✦&';
  function scramble(el, dur = 900) {
    const final = el.dataset.final || (el.dataset.final = el.textContent);
    const start = performance.now();
    const tick = now => {
      const p = Math.min(1, (now - start) / dur);
      const n = Math.floor(p * final.length);
      let out = final.slice(0, n);
      for (let i = n; i < final.length; i++) out += final[i] === ' ' ? ' ' : GLYPHS[(Math.random() * GLYPHS.length) | 0];
      el.textContent = out;
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
  $$('[data-scramble]').forEach(el => {
    ScrollTrigger.create({ trigger: el, start: 'top 88%', once: true, refreshPriority: -1, onEnter: () => scramble(el) });
  });

  /* ---------------- cursor ---------------- */
  if (fine) {
    const cur = $('.cursor');
    const dot = $('.cursor-dot'), ring = $('.cursor-ring');
    const dx = gsap.quickTo(dot, 'x', { duration: .12, ease: 'power3' });
    const dy = gsap.quickTo(dot, 'y', { duration: .12, ease: 'power3' });
    const rx = gsap.quickTo(ring, 'x', { duration: .5, ease: 'power3' });
    const ry = gsap.quickTo(ring, 'y', { duration: .5, ease: 'power3' });
    addEventListener('mousemove', e => { dx(e.clientX); dy(e.clientY); rx(e.clientX); ry(e.clientY); });
    $$('[data-cursor], a, button').forEach(el => {
      el.addEventListener('mouseenter', () => cur.classList.add('is-hover'));
      el.addEventListener('mouseleave', () => cur.classList.remove('is-hover'));
    });
  }

  /* ---------------- magnetic buttons ---------------- */
  if (fine && !reduce) {
    $$('.magnetic').forEach(btn => {
      const span = btn.querySelector('span');
      const bx = gsap.quickTo(btn, 'x', { duration: .6, ease: 'power3' });
      const by = gsap.quickTo(btn, 'y', { duration: .6, ease: 'power3' });
      const sx = span && gsap.quickTo(span, 'x', { duration: .6, ease: 'power3' });
      const sy = span && gsap.quickTo(span, 'y', { duration: .6, ease: 'power3' });
      btn.addEventListener('mousemove', e => {
        const r = btn.getBoundingClientRect();
        const mx = e.clientX - r.left - r.width / 2, my = e.clientY - r.top - r.height / 2;
        bx(mx * .3); by(my * .4);
        if (span) { sx(mx * .15); sy(my * .2); }
      });
      btn.addEventListener('mouseleave', () => { bx(0); by(0); if (span) { sx(0); sy(0); } });
    });
  }

  /* ---------------- particle fields (hero dust, shock sparks, feed drops) ---------------- */
  function field(canvas, mode, trigger) {
    if (!canvas || reduce) return;
    const ctx = canvas.getContext('2d');
    let w, h, dpr, parts = [], on = false, mx = 0, my = 0;
    const density = mode === 'shock' ? 9000 : mode === 'feed' ? 16000 : 14000;
    const spawn = (anyY = true) => ({
      x: Math.random() * w, y: anyY ? Math.random() * h : (mode === 'feed' ? -10 : h + 10),
      r: Math.random() * 1.6 + .3, z: Math.random() * .8 + .2,
      vy: mode === 'feed' ? Math.random() * .35 + .1 : -(Math.random() * .25 + .05),
      vx: (Math.random() - .5) * .1, t: Math.random() * Math.PI * 2,
      red: mode === 'dust' ? Math.random() < .18 : false, life: Math.random()
    });
    const resize = () => {
      dpr = Math.min(devicePixelRatio || 1, 2);
      w = canvas.clientWidth; h = canvas.clientHeight;
      if (!w || !h) return;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.round(Math.min(mode === 'shock' ? 160 : 110, w * h / density));
      parts = Array.from({ length: n }, () => spawn());
    };
    resize();
    addEventListener('resize', resize);
    addEventListener('mousemove', e => { mx = e.clientX / innerWidth - .5; my = e.clientY / innerHeight - .5; });
    const draw = () => {
      if (on && w) {
        ctx.clearRect(0, 0, w, h);
        for (const p of parts) {
          if (mode === 'shock') {
            // electric sparks: jittery, short-lived flickers with tiny streaks
            p.life -= .02 + p.z * .02;
            if (p.life <= 0) Object.assign(p, spawn(), { life: 1 });
            const a = Math.max(0, Math.sin(p.life * Math.PI)) * (Math.random() < .15 ? 1 : .55);
            const jx = (Math.random() - .5) * 3, jy = (Math.random() - .5) * 3;
            ctx.strokeStyle = Math.random() < .3 ? `rgba(255,255,255,${a})` : `rgba(255,140,155,${a})`;
            ctx.lineWidth = p.r * .9;
            ctx.beginPath();
            ctx.moveTo(p.x + jx, p.y + jy);
            ctx.lineTo(p.x + jx + (Math.random() - .5) * 10 * p.z, p.y + jy + (Math.random() - .5) * 10 * p.z);
            ctx.stroke();
            continue;
          }
          p.y += p.vy * p.z; p.x += p.vx; p.t += .03;
          if (mode === 'feed' && p.y > h + 10) Object.assign(p, spawn(false));
          if (mode !== 'feed' && p.y < -10) Object.assign(p, spawn(false));
          const a = (.35 + Math.sin(p.t) * .35) * p.z;
          const px = p.x + mx * 40 * p.z, py = p.y + my * 30 * p.z;
          ctx.beginPath();
          ctx.arc(px, py, p.r * p.z * (mode === 'feed' ? 2.2 : 1.4), 0, Math.PI * 2);
          ctx.fillStyle = mode === 'feed' ? `rgba(122,13,31,${a * .55})` : p.red ? `rgba(255,90,110,${a})` : `rgba(235,236,240,${a})`;
          ctx.fill();
        }
      }
      requestAnimationFrame(draw);
    };
    draw();
    ScrollTrigger.create({ trigger, start: 'top bottom', end: 'bottom top', refreshPriority: -1, onToggle: s => { on = s.isActive; if (on) resize(); } });
  }
  field($('.hero-dust'), 'dust', '.hero');
  $$('.sf-field').forEach(c => field(c, c.dataset.mode, '.sf'));

  /* ---------------- hero mouse parallax ---------------- */
  if (fine && !reduce) {
    const layers = $$('[data-depth]').map(el => ({
      d: parseFloat(el.dataset.depth),
      x: gsap.quickTo(el, 'x', { duration: 1.2, ease: 'power3' }),
      y: gsap.quickTo(el, 'y', { duration: 1.2, ease: 'power3' }),
      r: gsap.quickTo(el, 'rotation', { duration: 1.2, ease: 'power3' })
    }));
    addEventListener('mousemove', e => {
      const nx = e.clientX / innerWidth - .5, ny = e.clientY / innerHeight - .5;
      layers.forEach(l => { l.x(nx * l.d * 50); l.y(ny * l.d * 30); l.r(nx * l.d * 4); });
    });
  }

  /* ---------------- hero word rotator: Zszokuj. Nakarm. Powtórz. ---------------- */
  const rot = $$('.hero-rot span');
  if (rot.length) {
    let k = 0;
    rot[0].classList.add('on');
    setInterval(() => { rot[k].classList.remove('on'); k = (k + 1) % rot.length; rot[k].classList.add('on'); }, 1500);
  }

  /* ---------------- hero intro ---------------- */
  function heroIntro() {
    const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });
    tl.from('.hero-title .ch', { yPercent: 115, rotation: 6, duration: 1.6, stagger: .07 }, 0)
      .from('.hb--c', { y: '60vh', duration: 1.8 }, .15)
      .from('.hb--l', { y: '60vh', x: '10vw', rotation: -10, duration: 1.8 }, .3)
      .from('.hb--r', { y: '60vh', x: '-10vw', rotation: 10, duration: 1.8 }, .38)
      .from('.hero-glow', { opacity: 0, scale: .6, duration: 2 }, .2)
      .from('.hero-sub', { y: 30, opacity: 0, duration: 1.2 }, .8)
      .from('.hero-ctas .btn', { y: 30, opacity: 0, duration: 1.2, stagger: .1 }, .9)
      .from(['.nav', '.nav-cta', '.scroll-hint'], { opacity: 0, duration: 1 }, 1)
      .add(() => scramble($('.hero-kicker .hk-txt'), 1100), .5)
      .from('.hero-kicker', { opacity: 0, duration: .4 }, .5)
      .from('.hk-line', { scale: .6, opacity: 0, duration: 1, ease: 'back.out(2)' }, 1.4);
    return tl;
  }

  /* ---------------- loader / curtain ---------------- */
  function startPage() {
    root.classList.remove('is-loading');
    if (lenis) lenis.start();
    ScrollTrigger.refresh();
  }
  if (reduce || !loader || (window.Shopify && Shopify.designMode)) {
    loader && loader.remove();
    startPage();
  } else {
    const countEl = $('.loader-count'), lineEl = $('.loader-line i');
    const critical = $$('img[data-critical]');
    const state = { v: 0 };
    let done = 0;
    const total = critical.length + 1; // + fonts
    const show = () => {
      countEl.textContent = String(Math.round(state.v)).padStart(3, '0');
      lineEl.style.transform = `scaleX(${state.v / 100})`;
    };
    const bump = () => { done++; gsap.to(state, { v: (done / total) * 100, duration: .6, ease: 'power2.out', onUpdate: show }); };
    critical.forEach(img => (img.complete ? bump() : (img.addEventListener('load', bump, { once: true }), img.addEventListener('error', bump, { once: true }))));
    (document.fonts ? document.fonts.ready : Promise.resolve()).then(bump);

    const ready = new Promise(res => {
      const check = () => (done >= total ? res() : setTimeout(check, 80));
      check();
      setTimeout(res, 4500); // bezpiecznik
    });
    const minTime = new Promise(res => setTimeout(res, 1300));

    Promise.all([ready, minTime]).then(() => {
      const tl = gsap.timeline({ onComplete: () => loader.remove() });
      tl.to(state, { v: 100, duration: .4, onUpdate: show })
        .to('.loader-inner', { opacity: 0, y: -30, duration: .6, ease: 'power3.in' })
        .to('.curtain--l', { xPercent: -100, duration: 1.8, ease: 'expo.inOut' }, '-=.15')
        .to('.curtain--r', { xPercent: 100, duration: 1.8, ease: 'expo.inOut' }, '<')
        .fromTo('.curtain', { scaleY: 1 }, { scaleY: 1.06, transformOrigin: '50% 0', duration: 1.8, ease: 'expo.inOut' }, '<')
        .add(startPage, '-=1.2')
        .add(heroIntro(), '-=1.25');
    });
  }

  /* ---------------- hero scroll ---------------- */
  gsap.timeline({
    scrollTrigger: { trigger: '.hero', start: 'top top', end: '+=110%', scrub: 1, pin: true, anticipatePin: 1 }
  })
    .to('.hero-title', { scale: 1.5, yPercent: -18, opacity: .06, ease: 'none' }, 0)
    .to('.hb--l', { xPercent: -140, yPercent: 10, rotation: -18, opacity: 0, ease: 'none' }, 0)
    .to('.hb--r', { xPercent: 140, yPercent: 10, rotation: 18, opacity: 0, ease: 'none' }, 0)
    .to('.hb--c', { yPercent: -6, scale: 1.14, ease: 'none' }, 0)
    .to(['.hero-foot', '.hero-kicker', '.scroll-hint'], { opacity: 0, y: -50, ease: 'none', duration: .5 }, 0)
    .to('.hero-beams', { opacity: 0, ease: 'none', duration: .6 }, 0)
    .to('.hero-glow', { opacity: .4, ease: 'none' }, 0);

  /* ---------------- scroll progress ---------------- */
  gsap.to('.progress span', { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: .3 } });

  /* ---------------- manifesto: word-by-word ---------------- */
  const mt = $('[data-words]');
  if (mt) {
    const wrap = node => {
      [...node.childNodes].forEach(n => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach(part => {
            if (!part) return;
            if (/^\s+$/.test(part)) frag.append(part);
            else { const s = document.createElement('span'); s.className = 'w'; s.textContent = part; frag.append(s); }
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1) wrap(n);
      });
    };
    wrap(mt);
    gsap.to($$('.w', mt), {
      opacity: 1, stagger: .1, ease: 'none',
      scrollTrigger: { trigger: mt, start: 'top 78%', end: 'bottom 45%', scrub: true }
    });
  }

  /* ---------------- marquee: velocity skew ---------------- */
  const mq = $('.marquee-inner');
  if (mq && !reduce) {
    const skew = gsap.quickTo(mq, 'skewX', { duration: .5, ease: 'power3' });
    let t;
    ScrollTrigger.create({
      onUpdate: self => {
        skew(gsap.utils.clamp(-14, 14, self.getVelocity() / -250));
        clearTimeout(t); t = setTimeout(() => skew(0), 120);
      }
    });
    gsap.fromTo('.marquee', { xPercent: 3 }, { xPercent: -3, ease: 'none', scrollTrigger: { trigger: '.marquee', scrub: true } });
  }

  /* ---------------- SHOCK & FEED: scroll reveal + draggable split ---------------- */
  const sf = $('.sf');
  if (sf && !isMobile()) {
    const st = { base: 100, off: 0 };
    const apply = () => sf.style.setProperty('--split', Math.max(6, Math.min(94, st.base + st.off)) + '%');
    apply();
    gsap.timeline({
      scrollTrigger: { trigger: sf, start: 'top top', end: '+=80%', pin: true, scrub: 1, anticipatePin: 1 }
    })
      .to(st, { base: 50, ease: 'power2.inOut', onUpdate: apply }, 0)
      .from('.sf-feed .sf-copy', { x: 80, opacity: 0, ease: 'power2.out' }, .35)
      .from('.sf-bottle', { scale: .8, rotation: -8, ease: 'power2.out' }, 0);
    gsap.from('.sf-shock .sf-copy > *', {
      x: -60, opacity: 0, stagger: .08, duration: 1.1, ease: 'expo.out',
      scrollTrigger: { trigger: sf, start: 'top 70%' }
    });

    const offTo = gsap.quickTo(st, 'off', { duration: .6, ease: 'power3', onUpdate: apply });
    let drag = false;
    const move = e => {
      if (!drag) return;
      const r = sf.getBoundingClientRect();
      offTo((e.clientX - r.left) / r.width * 100 - st.base);
    };
    sf.addEventListener('pointerdown', e => { drag = true; sf.setPointerCapture(e.pointerId); move(e); });
    sf.addEventListener('pointermove', move);
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(ev => sf.addEventListener(ev, () => { drag = false; }));
  } else if (sf) {
    gsap.from('.sf-copy', { y: 50, opacity: 0, duration: 1.1, ease: 'expo.out', stagger: .2, scrollTrigger: { trigger: sf, start: 'top 70%' } });
  }

  /* ---------------- acts: metoda cegiełkowa (pinned product story) ---------------- */
  const stage = $('.acts-stage');
  if (stage) {
    const avs = $$('.av', stage), copies = $$('.ac', stage), bgs = $$('.acts-bg', stage);
    const nums = $$('.acts-numeral span', stage), steps = $$('.acts-steps li', stage);
    const bar = $('.acts-bar span', stage);
    const bricks = $$('.am-bricks i', stage), amVal = $('.am-val', stage);
    const mobile = isMobile();
    const boxShift = mobile ? 34 : 50;

    gsap.set(avs.slice(1), { autoAlpha: 0 });
    gsap.set(copies.slice(1), { autoAlpha: 0 });
    gsap.set(bgs.slice(1), { opacity: 0 });
    gsap.set(nums.slice(1), { autoAlpha: 0 });

    const tl = gsap.timeline({ defaults: { ease: 'power2.inOut' } });
    avs.forEach((av, i) => {
      const box = $('.av-box', av), bottle = $('.av-bottle', av);
      if (i > 0) {
        const p = avs[i - 1], L = 't' + i;
        tl.addLabel(L)
          .to($('.av-bottle', p), { yPercent: -70, xPercent: -40, rotation: -14, autoAlpha: 0, duration: 1 }, L)
          .to($('.av-box', p), { yPercent: -50, xPercent: boxShift + 60, rotation: 22, autoAlpha: 0, duration: 1 }, L)
          .to(copies[i - 1], { y: -60, autoAlpha: 0, duration: .55 }, L)
          .to(nums[i - 1], { yPercent: -25, autoAlpha: 0, duration: .7 }, L)
          .to(bgs[i], { opacity: 1, duration: 1, ease: 'none' }, L)
          .set(av, { autoAlpha: 1 }, L)
          .fromTo(bottle, { yPercent: 90, rotation: 14, autoAlpha: 0 }, { yPercent: 0, rotation: 0, autoAlpha: 1, duration: 1, ease: 'power3.out' }, L + '+=.3')
          .fromTo(nums[i], { yPercent: 25, autoAlpha: 0 }, { yPercent: 0, autoAlpha: 1, duration: .8 }, L + '+=.35')
          .fromTo(copies[i], { y: 70, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: .6, ease: 'power3.out' }, L + '+=.55');
      }
      tl.fromTo(box, { xPercent: 0, rotation: 0, scale: .9 }, { xPercent: boxShift, rotation: 7, scale: 1, duration: 1, ease: 'power3.out' })
        .fromTo(bottle, { xPercent: 0 }, { xPercent: mobile ? -18 : -14, duration: 1, ease: 'power3.out' }, '<')
        .fromTo($$('.claims li', copies[i]), { x: -24, autoAlpha: 0 }, { x: 0, autoAlpha: 1, stagger: .14, duration: .45 }, '<+.15')
        .to({}, { duration: .9 });
    });

    const dur = tl.duration();
    const cuts = avs.slice(1).map((_, k) => (tl.labels['t' + (k + 1)] + .5) / dur);
    let current = -1;
    const setAct = a => {
      if (a === current) return;
      current = a;
      stage.dataset.act = a;
      steps.forEach((s, k) => s.classList.toggle('is-active', k === a));
      bricks.forEach((b, k) => b.classList.toggle('on', k <= a));
      amVal.textContent = '×' + (a + 1);
      gsap.fromTo(amVal, { scale: 1.4 }, { scale: 1, duration: .5, ease: 'back.out(3)' });
    };
    setAct(0);
    ScrollTrigger.create({
      trigger: '.acts', start: 'top top', end: () => '+=' + innerHeight * 3.4,
      pin: stage, scrub: 1, anticipatePin: 1, animation: tl,
      onUpdate: self => {
        const p = self.progress;
        bar.style.transform = `scaleX(${p})`;
        let a = 0; cuts.forEach(c => { if (p >= c) a++; });
        setAct(a);
      }
    });

    if (fine && !reduce) {
      const vis = $('.acts-visual', stage);
      const rx = gsap.quickTo(vis, 'rotationY', { duration: 1, ease: 'power3' });
      const ry = gsap.quickTo(vis, 'rotationX', { duration: 1, ease: 'power3' });
      gsap.set(vis, { transformPerspective: 1400 });
      stage.addEventListener('mousemove', e => { rx((e.clientX / innerWidth - .5) * 10); ry((e.clientY / innerHeight - .5) * -6); });
    }
  }

  /* ---------------- stats: count-up ---------------- */
  $$('.stat').forEach((s, i) => {
    const b = $('[data-to]', s);
    const to = +b.dataset.to, pre = b.dataset.prefix || '';
    const o = { v: 0 };
    b.textContent = pre + '0';
    gsap.from(s, { y: 60, opacity: 0, duration: 1.2, ease: 'expo.out', delay: i * .08, scrollTrigger: { trigger: '.stats', start: 'top 80%' } });
    ScrollTrigger.create({
      trigger: '.stats', start: 'top 75%', once: true,
      onEnter: () => gsap.to(o, { v: to, duration: 1.8, delay: i * .12, ease: 'power3.out', onUpdate: () => { b.textContent = pre + Math.round(o.v); } })
    });
  });

  /* ---------------- gallery: horizontal scroll ---------------- */
  const track = $('.gallery-track');
  if (track) {
    const dist = () => track.scrollWidth - innerWidth;
    const hTween = gsap.to(track, {
      x: () => -dist(), ease: 'none',
      scrollTrigger: { trigger: '.gallery', start: 'top top', end: () => '+=' + dist(), pin: true, scrub: 1, invalidateOnRefresh: true, anticipatePin: 1 }
    });
    $$('.g-img img', track).forEach(img => {
      gsap.fromTo(img, { xPercent: 9 }, {
        xPercent: -9, ease: 'none',
        scrollTrigger: { trigger: img.closest('.g-item'), containerAnimation: hTween, start: 'left right', end: 'right left', scrub: true }
      });
    });
    $$('.g-item', track).forEach(it => {
      gsap.from(it, {
        clipPath: 'inset(100% 0% 0% 0%)', ease: 'none',
        scrollTrigger: { trigger: it, containerAnimation: hTween, start: 'left 95%', end: 'left 55%', scrub: true }
      });
    });
    gsap.from('.g-quote', {
      y: 80, opacity: 0, ease: 'none',
      scrollTrigger: { trigger: '.g-quote', containerAnimation: hTween, start: 'left 90%', end: 'left 50%', scrub: true }
    });
    gsap.fromTo('.g-outro .outline', { xPercent: 30 }, {
      xPercent: 0, ease: 'none',
      scrollTrigger: { trigger: '.g-outro', containerAnimation: hTween, start: 'left right', end: 'right right', scrub: true }
    });
  }

  /* ---------------- routines: tabs ---------------- */
  const rt = $('.rt');
  if (rt) {
    const tabs = $$('.rt-tab', rt), panels = $$('.rt-panel', rt), ind = $('.rt-ind', rt);
    const place = (tab, instant) => {
      const box = tab.parentElement.getBoundingClientRect(), r = tab.getBoundingClientRect();
      gsap.to(ind, { x: r.left - box.left, y: r.top - box.top, width: r.width, height: r.height, duration: instant ? 0 : .6, ease: 'expo.out' });
    };
    const select = i => {
      tabs.forEach((t, k) => { t.classList.toggle('is-active', k === i); t.setAttribute('aria-selected', k === i); });
      panels.forEach((p, k) => p.classList.toggle('is-active', k === i));
      place(tabs[i]);
      gsap.fromTo($$('.rt-steps li', panels[i]), { y: 40, opacity: 0, rotationX: -10 }, { y: 0, opacity: 1, rotationX: 0, duration: .9, stagger: .09, ease: 'expo.out', transformPerspective: 800 });
      gsap.fromTo($$('.rt-steps img', panels[i]), { yPercent: 30, rotation: -8 }, { yPercent: 0, rotation: 0, duration: 1.1, stagger: .09, ease: 'expo.out' });
    };
    tabs.forEach((t, i) => t.addEventListener('click', () => select(i)));
    rt.querySelector('.rt-tabs').addEventListener('keydown', e => {
      const cur = tabs.findIndex(t => t.classList.contains('is-active'));
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const n = (cur + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[n].focus(); select(n);
      }
    });
    place(tabs[0], true);
    addEventListener('resize', () => place(tabs.find(t => t.classList.contains('is-active')), true));
    document.fonts && document.fonts.ready.then(() => place(tabs.find(t => t.classList.contains('is-active')), true));
    ScrollTrigger.create({ trigger: rt, start: 'top 75%', once: true, onEnter: () => select(tabs.findIndex(t => t.classList.contains('is-active'))) });
  }

  /* ---------------- generic reveals ---------------- */
  $$('[data-reveal]').forEach(el => {
    gsap.from(el, { y: 50, opacity: 0, duration: 1.2, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 88%' } });
  });

  /* ---------------- cast cards ---------------- */
  // set start state once, then always animate *to* the end state (safe if onEnter fires twice)
  const batchReveal = (sel, from, opts = {}) => {
    gsap.set(sel, from);
    ScrollTrigger.batch(sel, {
      start: opts.start || 'top 88%', once: true,
      onEnter: els => gsap.to(els, { y: 0, opacity: 1, rotation: 0, rotationX: 0, rotationY: 0, duration: opts.duration || 1.2, ease: 'expo.out', stagger: opts.stagger || .09, overwrite: true })
    });
  };
  batchReveal('.cc', { y: 70, opacity: 0, rotationX: -12, transformPerspective: 900 });
  $$('.cc').forEach(c => c.addEventListener('pointermove', e => {
    const r = c.getBoundingClientRect();
    c.style.setProperty('--mx', (e.clientX - r.left) + 'px');
    c.style.setProperty('--my', (e.clientY - r.top) + 'px');
  }));
  // smooth-scroll friendly: refresh pins when an INCI accordion changes page height
  $$('.inci details').forEach(d => d.addEventListener('toggle', () => ScrollTrigger.refresh()));

  /* ---------------- HAIR SCIENCE: follicular transport ---------------- */
  const sci = $('.science');
  if (sci) {
    const svg = $('.sci-svg', sci);
    const NS = 'http://www.w3.org/2000/svg';
    const SURF = 184;
    const paths = $$('.sci-path', svg);
    const lens = paths.map(p => p.getTotalLength());
    const entry = paths.map((p, i) => { for (let l = 0; l < lens[i]; l += 2) if (p.getPointAtLength(l).y >= SURF) return l; return 0; });
    const entryPt = paths.map((p, i) => p.getPointAtLength(entry[i]));
    const halos = $$('.sci-halo', svg);
    const steps = $$('.sci-step', sci);
    const g = $('.sci-particles', svg);
    // neutral tones — particles stand for "składniki" in general, not specific ingredients
    const colors = ['#e9eaee', '#f2a7b3'];
    const parts = [];
    const perF = [0, 0, 0];
    // glow only the upper/middle part of each follicle (its surroundings), never the bulb
    halos.forEach((h, i) => {
      const seg = (lens[i] - entry[i]) * .62;
      h.style.strokeDasharray = `0 ${entry[i]} ${seg} ${lens[i]}`;
    });
    for (let k = 0; k < 66; k++) {
      const fi = k % 3;
      const toF = Math.random() < .7;
      const c = document.createElementNS(NS, 'circle');
      const r = Math.random() * 2.5 + 3;
      c.setAttribute('r', r.toFixed(1));
      c.setAttribute('fill', colors[(Math.random() * colors.length) | 0]);
      c.setAttribute('opacity', 0);
      g.appendChild(c);
      const sx = entryPt[fi].x + (Math.random() - .5) * 260;
      if (toF) perF[fi]++;
      parts.push({
        c, fi, toF, r, sx, sy: 20 + Math.random() * 110, tx: sx + (Math.random() - .5) * 30,
        d: Math.random() * .52, j: (Math.random() - .5) * 16,
        depth: .08 + Math.random() * .45,                       // how far down the follicle (fraction), stays well above the bulb
        side: (Math.random() < .5 ? -1 : 1) * (26 + Math.random() * 38) // how far it diffuses out into the surrounding skin
      });
    }
    const render = p => {
      const arrived = [0, 0, 0];
      for (const q of parts) {
        const t = clamp01((p - q.d) / .4);
        let x, y, o, rr = q.r;
        if (q.toF) {
          const L = lerp(entry[q.fi], lens[q.fi], q.depth);
          if (t < .3) {
            // fall onto the follicle opening
            const u = easeInOut(t / .3);
            x = lerp(q.sx, entryPt[q.fi].x, u); y = lerp(q.sy, entryPt[q.fi].y, u);
          } else if (t < .65) {
            // slip into the follicle channel
            const u = easeInOut((t - .3) / .35);
            const pt = paths[q.fi].getPointAtLength(lerp(entry[q.fi], L, u));
            x = pt.x + q.j * (1 - u) * .4; y = pt.y;
          } else {
            // diffuse sideways into the tissue around the follicle
            const u = easeOut((t - .65) / .35);
            const a = paths[q.fi].getPointAtLength(L), b = paths[q.fi].getPointAtLength(Math.min(lens[q.fi], L + 1));
            const dx = b.x - a.x, dy = b.y - a.y, n = Math.hypot(dx, dy) || 1;
            x = a.x + (-dy / n) * q.side * u; y = a.y + (dx / n) * q.side * u + 6 * u;
            rr = q.r * (1 + u * .6);
          }
          if (t >= 1) arrived[q.fi]++;
          o = Math.min(1, t * 8) * (t > .65 ? lerp(1, .4, (t - .65) / .35) : 1);
        } else {
          const u = easeInOut(Math.min(1, t / .45));
          x = lerp(q.sx, q.tx, u); y = lerp(q.sy, SURF - 3 + Math.abs(q.j) * .2, u);
          o = Math.min(1, t * 8) * (t > .45 ? lerp(1, .22, (t - .45) / .55) : 1);
        }
        q.c.setAttribute('cx', x.toFixed(1));
        q.c.setAttribute('cy', y.toFixed(1));
        q.c.setAttribute('r', rr.toFixed(1));
        q.c.setAttribute('opacity', o.toFixed(2));
      }
      halos.forEach((h, i) => { h.style.opacity = (.32 * arrived[i] / (perF[i] || 1)).toFixed(3); });
      const idx = Math.min(steps.length - 1, Math.floor(p * steps.length * .999));
      steps.forEach((s, i) => s.classList.toggle('is-active', i === idx));
    };
    render(0);
    const state = { p: 0 };
    gsap.to(state, {
      p: 1, ease: 'none', onUpdate: () => render(state.p),
      scrollTrigger: { trigger: sci, start: 'top top', end: '+=240%', pin: '.sci-stage', scrub: 1, anticipatePin: 1 }
    });
    // label lines draw-in
    $$('.sci-lbl-line', svg).forEach(l => {
      const len = l.getTotalLength();
      gsap.fromTo(l, { strokeDashoffset: len, strokeDasharray: `${len} ${len}` }, {
        strokeDashoffset: 0, duration: 1.2, ease: 'power2.out',
        scrollTrigger: { trigger: sci, start: 'top 60%' },
        onComplete: () => l.style.strokeDasharray = '3 4'
      });
    });
    gsap.from('.sci-labels text', { opacity: 0, duration: 1, stagger: .15, scrollTrigger: { trigger: sci, start: 'top 55%' } });
  }

  /* ---------------- myths: flip cards ---------------- */
  $$('.myth').forEach(m => m.addEventListener('click', () => m.classList.toggle('is-flipped')));
  batchReveal('.myth', { y: 80, opacity: 0, rotationY: -25, transformPerspective: 1000 }, { start: 'top 90%', duration: 1.3, stagger: .08 });

  /* ---------------- scent lines ---------------- */
  gsap.from('.sl > span', {
    yPercent: 110, duration: 1.4, ease: 'expo.out', stagger: .14,
    scrollTrigger: { trigger: '.scent-lines', start: 'top 80%' }
  });

  /* ---------------- shop cards: reveal + 3D tilt ---------------- */
  batchReveal('.card', { y: 120, opacity: 0, rotation: 3 }, { start: 'top 90%', duration: 1.4, stagger: .12 });
  if (fine && !reduce) {
    $$('.card').forEach(card => {
      const inner = $('.card-in', card);
      const rx = gsap.quickTo(inner, 'rotationX', { duration: .6, ease: 'power3' });
      const ry = gsap.quickTo(inner, 'rotationY', { duration: .6, ease: 'power3' });
      card.addEventListener('pointermove', e => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        ry((px - .5) * 14); rx((.5 - py) * 12);
        inner.style.setProperty('--gx', px * 100 + '%');
        inner.style.setProperty('--gy', py * 100 + '%');
      });
      card.addEventListener('pointerleave', () => { rx(0); ry(0); });
    });
  }
  $$('.card-media img').forEach(img => {
    gsap.fromTo(img, { yPercent: 8 }, { yPercent: -8, ease: 'none', scrollTrigger: { trigger: img.closest('.card'), scrub: true } });
  });

  /* ---------------- quote: photo reveal + parallax ---------------- */
  if ($('.quote-photo')) {
    gsap.from('.qp-frame', {
      clipPath: 'inset(100% 0% 0% 0%)', duration: 1.6, ease: 'expo.out',
      scrollTrigger: { trigger: '.quote', start: 'top 75%' }
    });
    gsap.fromTo('.qp-frame img', { yPercent: -8, scale: 1.12 }, {
      yPercent: 8, scale: 1, ease: 'none', scrollTrigger: { trigger: '.quote', scrub: true }
    });
  }

  /* ---------------- launch: curtains open as you scroll in ---------------- */
  if ($('.launch')) {
    gsap.timeline({ scrollTrigger: { trigger: '.launch', start: 'top 85%', end: 'top 10%', scrub: 1 } })
      .fromTo('.lc--l', { xPercent: 0 }, { xPercent: -100, ease: 'power2.inOut' }, 0)
      .fromTo('.lc--r', { xPercent: 0 }, { xPercent: 100, ease: 'power2.inOut' }, 0)
      .fromTo('.launch-bg img', { scale: 1.25 }, { scale: 1.05, ease: 'none' }, 0)
      .from('.launch-title', { scale: .85, opacity: 0, ease: 'power2.out' }, .35)
      .from(['.launch-lead', '.launch-ctas', '.launch-steps'], { y: 40, opacity: 0, stagger: .08, ease: 'power2.out' }, .5);
  }

  /* ---------------- footer ---------------- */
  gsap.from('.footer-big', {
    yPercent: 40, opacity: 0, ease: 'none',
    scrollTrigger: { trigger: '.footer', start: 'top bottom', end: 'bottom bottom', scrub: true }
  });


  /* ---------------- Shopify: add to cart (AJAX) ---------------- */
  (() => {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const toast = $('.mt-toast'), msg = $('.mt-toast-msg', toast || document);
    const navCta = $('.nav-cta');
    let hideT;
    const showToast = (text, error) => {
      if (!toast) return;
      msg.textContent = text;
      toast.classList.toggle('is-error', !!error);
      toast.classList.add('is-on');
      clearTimeout(hideT); hideT = setTimeout(() => toast.classList.remove('is-on'), 4500);
    };
    const setCount = n => {
      if (!navCta || !n) return;
      navCta.href = navCta.dataset.cartUrl || root + 'cart';
      $('span', navCta).textContent = 'Koszyk · ' + n;
    };
    $$('[data-add-to-cart]').forEach(btn => btn.addEventListener('click', async e => {
      const ids = (btn.dataset.addToCart || '').split(',').map(s => s.trim()).filter(Boolean);
      if (!ids.length) return; // no product configured -> follow the link
      e.preventDefault();
      e.stopImmediatePropagation();
      btn.classList.add('is-busy');
      try {
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map(id => ({ id: Number(id), quantity: 1 })) })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Błąd');
        const cart = await (await fetch(root + 'cart.js')).json();
        setCount(cart.item_count);
        showToast(ids.length > 1 ? 'Rytuał dodany do koszyka' : 'Dodano do koszyka');
        const label = $('span', btn), prev = label.textContent;
        label.textContent = 'Dodano ✓';
        setTimeout(() => { label.textContent = prev; }, 2200);
      } catch (err) {
        showToast(err.message || 'Nie udało się dodać do koszyka', true);
      } finally {
        btn.classList.remove('is-busy');
      }
    }, true));
  })();

  addEventListener('load', () => ScrollTrigger.refresh());
})();
